import {
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
  useInfiniteQuery,
  useQuery
} from '@tanstack/react-query'
import type { AclsStructure } from '~/components/Layout/GlobalMenu/menu'
import type { TaxonomyItem } from '~/domains/taxonomies/types'
import type { Agency } from '~/domains/users/types'
import type { UserGroup } from '~/types/global'
import {
  fetchApiWithToken,
  getUserIdFromContext,
  hydraNextPageParam,
  ResponseError,
  type WithHydraPagination
} from '../utils'

export interface UserInfosResponse {
  email: string
  id: number
  firstname?: string
  lastname: string
  phone: string
  civility: string
  function?: string
  linkedin?: string
  isSuperUser: boolean
  isAgencyManager: boolean
  groups: UserGroup[]
  agencies: {
    id: number
    name: string
  }[]
  groupMain: UserGroup
  notificationUnreadCount: number
}

export type UserInfoLDResponse = WithHydraPagination<UserInfosResponse>

export interface NextEvent {
  id: number
  date: string
  type: number
  readableType: string
  meetingLink: string | null
  link: string
  recipients: string[]
}

export const useUsers = () =>
  useQuery({
    queryKey: ['users'],
    queryFn: () =>
      fetchApiWithToken<UserInfosResponse[]>({
        method: 'get',
        url: '/v1/users'
      }),
    refetchOnWindowFocus: false,
    staleTime: 1800_000
  })

export const useUserData = (userId: number | null | number[]) => {
  const userIds = Array.isArray(userId) ? userId : [userId]
  return useQuery({
    queryKey: ['user', userId],
    queryFn: () =>
      fetchApiWithToken<UserInfosResponse[]>({
        method: 'get',
        url: `/v1/users?id[]=${userIds.join('&id[]=')}`
      }),
    enabled: !!userId,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    retry: 3
  })
}

const useUsersSearch = ({
  ids,
  search,
  listingContext,
  enabled = true,
  fields
}: {
  ids?: string[]
  search?: string
  listingContext?: string
  enabled?: boolean
  fields?: string[]
}) =>
  useInfiniteQuery<UserInfoLDResponse, Error, InfiniteData<UserInfoLDResponse>>(
    {
      queryKey: ['users', ids, search, listingContext, fields],
      initialPageParam: 1,
      getNextPageParam: hydraNextPageParam(),
      queryFn: async ({ pageParam }) => {
        const queryParams = new URLSearchParams()
        if (ids) {
          for (const id of ids) {
            queryParams.append('id[]', id.toString())
          }
        }
        if (search) {
          queryParams.append('fullname', search)
        }
        if (listingContext) {
          queryParams.append('listingContext', listingContext)
        }
        queryParams.append('order[lastname]', 'asc')
        queryParams.append('order[firstname]', 'asc')
        for (const field of fields ?? []) {
          queryParams.append('fields[]', field)
        }
        return (await fetchApiWithToken({
          method: 'get',
          url: `/v1/users?${queryParams.toString()}&page=${pageParam}`,
          useLDJson: true
        })) as UserInfoLDResponse
      },
      enabled,
      staleTime: 60_000
    }
  )

export type UsersAutocompleteResponse = {
  id: number
  firstname: string
  lastname: string
  function?: string
  agencies: Agency[]
}

export const useUsersAutocomplete = ({
  ids,
  search,
  listingContext,
  enabled = true
}: {
  ids?: string[]
  search?: string
  listingContext?: string
  enabled?: boolean
}) =>
  useUsersSearch({
    ids,
    search,
    listingContext,
    enabled,
    fields: ['id', 'firstname', 'lastname', 'function', 'agencies']
  }) as unknown as UseInfiniteQueryResult<
    InfiniteData<WithHydraPagination<UsersAutocompleteResponse>>
  >

export const useCurrentUserInfos = () => {
  let userId: number | undefined = Number(localStorage.getItem('id'))
  if (!userId) {
    userId = window.JobConfig?.user.id
  }
  return useQuery({
    queryKey: ['user', userId],
    queryFn: () =>
      fetchApiWithToken<UserInfosResponse[]>({
        method: 'get',
        url: `/v1/users?id=${String(userId)}`
      }),
    refetchOnWindowFocus: false,
    staleTime: 1800_000,
    enabled: userId !== undefined
  })
}

export const useMeInfos = (): UserInfosResponse | null => {
  const { data } = useCurrentUserInfos()
  return data?.[0] ?? null
}

export const useUserTokenRefresh = () => {
  const id = getUserIdFromContext()
  return useQuery({
    queryKey: ['user', 'token'],
    queryFn: () =>
      fetchApiWithToken<{ token: string }>({
        method: 'get',
        url: `/v1/users/${String(id)}/token`
      }),
    refetchInterval: 1800_000,
    refetchOnWindowFocus: false,
    enabled: id !== null && id !== ''
  })
}

export const useUserAcls = (id: number | null) =>
  useQuery({
    queryKey: ['user', id, 'acls'],
    queryFn: () =>
      fetchApiWithToken<AclsStructure>({
        method: 'get',
        url: `/v1/users/${id ?? ''}/acl`
      }),
    enabled: id !== null,
    refetchOnWindowFocus: false,
    staleTime: 1800_000
  })

export const useUserSearchToken = () => {
  const userId = getUserIdFromContext()
  return useQuery({
    queryKey: ['user', 'searchToken', userId],
    queryFn: () =>
      fetchApiWithToken<{ token: string }>({
        method: 'get',
        url: `/v1/users/${String(userId)}/search-token`
      }),
    staleTime: 3600_000,
    refetchOnWindowFocus: false,
    enabled: userId !== null
  })
}

export type UserNotificationType =
  | 'application'
  | 'application_error'
  | 'sms_reply'
  | 'stats_file'
  | 'sequence_added_recipients'
  | 'gdpr_anonymized_applicants'
  | 'test_completed'
  | 'meeting_reported'
  | 'soon_event'
  | 'factor_answer'
  | 'resume_inbound'
  | 'availability_response'
  | 'email_received'
  | 'shortlist_submitted'

type UserBaseNotification = {
  id: number
  createdAt: string
  updatedAt: string
  slug: string
  isRead: boolean
}

type BaseNotificationMetadata = {
  details: {
    [key: string]: string | number | string[] | null
  }
}

type StackableNotificationsMetadata = {
  count: number
  stack: {
    [key: string]: string
  }[]
}

type ApplicationNotification = UserBaseNotification & {
  type: 'application'
  metadata: BaseNotificationMetadata &
    StackableNotificationsMetadata & {
      details: {
        offerName: string
        offerId: string
      }
      stack: { applicantName?: string }[]
    }
}

type ApplicationErrorNotification = UserBaseNotification & {
  type: 'application_error'
  metadata: BaseNotificationMetadata &
    StackableNotificationsMetadata & {
      details: {
        reason: string
        // Both are null for a spontaneous application
        offerName: string | null
        offerId: number | null
      }
      stack: {
        jobmailId: string
        applicantName?: string
      }[]
    }
}

type SmsReplyNotification = UserBaseNotification & {
  type: 'sms_reply'
  metadata: BaseNotificationMetadata &
    StackableNotificationsMetadata & {
      details: {
        threadId: string
        applicantId?: number | null
        contactId?: number | null
      }
      stack: { senderName?: string; content?: string }[]
    }
}

type StatsFileNotification = UserBaseNotification & {
  type: 'stats_file'
  metadata: BaseNotificationMetadata &
    StackableNotificationsMetadata & {
      stack: { name?: string; period?: string }[]
    }
}

type SequenceAddedRecipientsNotification = UserBaseNotification & {
  type: 'sequence_added_recipients'
  metadata: BaseNotificationMetadata & {
    details: {
      sequenceName?: string
      count: string
    }
  }
}

type TestCompletedNotification = UserBaseNotification & {
  type: 'test_completed'
  metadata: BaseNotificationMetadata & {
    details: {
      testName: string
      applicantId: string
    }
  }
}

type ShortlistSubmittedNotification = UserBaseNotification & {
  type: 'shortlist_submitted'
  metadata: BaseNotificationMetadata & {
    details: {
      orderId: number
      contactName: string
      applicantCount: number
    }
  }
}

type MeetingReportedNotification = UserBaseNotification & {
  type: 'meeting_reported'
  metadata: BaseNotificationMetadata & {
    details: {
      meetingId: number
      title: string
      applicantId: number | null
      contactId?: number | null
      participantName: string
      date?: string | null
    }
  }
}

type GdprAnonymizedApplicantsNotification = UserBaseNotification & {
  type: 'gdpr_anonymized_applicants'
  metadata: BaseNotificationMetadata & {
    details: {
      count: string
    }
  }
}

type SoonEventNotification = UserBaseNotification & {
  type: 'soon_event'
  metadata: BaseNotificationMetadata & {
    details: {
      type: number
      readableType: string
      meetingLink: string | null
      date: string
      link: string
      recipients: string[]
    }
  }
}

type FactorAnswerNotification = UserBaseNotification & {
  type: 'factor_answer'
  metadata: BaseNotificationMetadata &
    StackableNotificationsMetadata & {
      details: {
        company: string
        request_type: string
      }
      stack: {
        factor_request_id?: string
        answer?: string
        timestamp?: string
      }[]
    }
}

type ResumeInboundNotification = UserBaseNotification & {
  type: 'resume_inbound'
  metadata: BaseNotificationMetadata & {
    details: {
      applicantId: string
      applicant: string
    }
    stack: {
      cvSource?: TaxonomyItem<'cv_sources'>
      agency?: Agency
    }[]
  }
}

type AvailabilityResponseNotification = UserBaseNotification & {
  type: 'availability_response'
  metadata: BaseNotificationMetadata & {
    details: {
      applicantId: StringConstructor
    }
    stack: {
      available: boolean | null
      senderName?: string
      availabilityLabel?: string
      content?: string
    }[]
  }
}

type EmailReceivedNotification = UserBaseNotification & {
  type: 'email_received'
  metadata: BaseNotificationMetadata & {
    details: {
      applicantId?: number
      contactId?: number
      companyId?: number
    }
    stack: {
      sender?: string
      subject?: string
      content?: string
    }[]
  }
}

export type UserNotification =
  | ApplicationNotification
  | ApplicationErrorNotification
  | SmsReplyNotification
  | StatsFileNotification
  | SequenceAddedRecipientsNotification
  | GdprAnonymizedApplicantsNotification
  | TestCompletedNotification
  | MeetingReportedNotification
  | ShortlistSubmittedNotification
  | SoonEventNotification
  | FactorAnswerNotification
  | ResumeInboundNotification
  | AvailabilityResponseNotification
  | EmailReceivedNotification
type RealTimeNotification<T extends UserBaseNotification> = Omit<
  T,
  'createdAt' | 'updatedAt' | 'isRead'
> & { unreadCount: number }

export type RealTimeUserNotification =
  | RealTimeNotification<ApplicationNotification>
  | RealTimeNotification<ApplicationErrorNotification>
  | RealTimeNotification<SmsReplyNotification>
  | RealTimeNotification<StatsFileNotification>
  | RealTimeNotification<SequenceAddedRecipientsNotification>
  | RealTimeNotification<GdprAnonymizedApplicantsNotification>
  | RealTimeNotification<TestCompletedNotification>
  | RealTimeNotification<MeetingReportedNotification>
  | RealTimeNotification<ShortlistSubmittedNotification>
  | RealTimeNotification<SoonEventNotification>
  | RealTimeNotification<FactorAnswerNotification>
  | RealTimeNotification<ResumeInboundNotification>
  | RealTimeNotification<AvailabilityResponseNotification>
  | RealTimeNotification<EmailReceivedNotification>

export type UserNotificationsLDResponse = {
  'hydra:member': UserNotification[]
  'hydra:totalItems': number
}

export const useCurrentUserNotifications = ({
  userId,
  active = false
}: {
  userId: number
  active?: boolean
}): {
  userNotifications: UserNotification[] | undefined
  userNotificationsTotal: number | undefined
  isLoading: boolean
  isError: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
} => {
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError
  } = useInfiniteQuery<
    UserNotificationsLDResponse,
    Error,
    InfiniteData<UserNotificationsLDResponse>
  >({
    queryKey: ['user', 'notifications', userId],
    initialPageParam: 1,
    getNextPageParam: hydraNextPageParam(),
    queryFn: async ({ pageParam }) => {
      return (await fetchApiWithToken({
        method: 'get',
        url: `/v1/users/${userId}/notifications?order[isRead]=asc&order[updatedAt]=desc&page=${pageParam}`,
        useLDJson: true
      })) as UserNotificationsLDResponse
    },
    enabled: !!userId && active,
    staleTime: 60_000
  })
  return {
    userNotifications: data?.pages.flatMap((page) => page['hydra:member']),
    userNotificationsTotal: data?.pages[0]?.['hydra:totalItems'],
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  }
}

export const useNextEvent = ({
  userId,
  dismissedEventId
}: {
  userId: number
  dismissedEventId: number | null
}): UseQueryResult<NextEvent | null> =>
  useQuery<NextEvent | null>({
    queryKey: ['user', userId, 'next_event'],
    queryFn: async () => {
      const url = `/v1/users/${userId}/next_event${dismissedEventId ? `?dismissedEventId=${dismissedEventId}` : ''}`
      const res = await fetchApiWithToken<NextEvent | NextEvent[]>({
        method: 'GET',
        url: url
      })

      return Array.isArray(res) ? null : res
    },
    retry: (failureCount, error) => {
      if (error instanceof ResponseError && error.httpStatus !== 200) {
        return false
      }
      return failureCount < 3
    },
    refetchOnWindowFocus: true,
    staleTime: 60_000
  })
